import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AffPayment, AffUserData, PaymentTypeEnum } from '@/api/affiliate/types/Affiliate'
import { AppDialog } from '@/components/AppDialog'
import { ArrowLeftIcon } from '@/components/icons/ArrowLeftIcon'
import CouponIcon from '@/components/icons/coupon.svg?react'
import GiftCardIcon from '@/components/icons/gift-card.svg?react'
import LinkIcon from '@/components/icons/link.svg?react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface AffiliateProfitsTabProps {
  refUserData: AffUserData
}

const paymentTypeConfigMap = {
  [PaymentTypeEnum.Shipments]: {
    labelKey: 'affiliate.paymentTypeLink' as const,
    Icon: LinkIcon,
    iconColorClass: 'text-[#1EB4FF]',
    cardBgClass: 'bg-[#E3F4FC]',
  },
  [PaymentTypeEnum.Coupon]: {
    labelKey: 'affiliate.paymentTypeCoupon' as const,
    Icon: CouponIcon,
    iconColorClass: 'text-[#FAD600]',
    cardBgClass: 'bg-[#FFF7DF]',
  },
  [PaymentTypeEnum.Giftcard]: {
    labelKey: 'affiliate.paymentTypeGiftcard' as const,
    Icon: GiftCardIcon,
    iconColorClass: 'text-[#A98F5B]',
    cardBgClass: 'bg-[#F2F0EC]',
  },
}

interface PaymentDetailsDialogProps {
  paymentType: PaymentTypeEnum
  payments: AffPayment[]
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const PaymentDetailsDialog = ({ paymentType, payments, isOpen, onOpenChange }: PaymentDetailsDialogProps) => {
  const { t } = useTranslation()
  const config = paymentTypeConfigMap[paymentType]
  const title = t(config.labelKey)
  const Icon = config.Icon

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('he-IL')
  }

  return (
    <AppDialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className='mb-6 flex flex-col items-center justify-center gap-4'>
        <Icon className={`size-6 ${config.iconColorClass}`} />
        <div className='text-text-primary text-lg font-medium'>{title}</div>
      </div>
      <div>
        <div className='bg-background-secondary border-border-default grid grid-cols-2 gap-4 border-b p-3'>
          <div className='text-text-secondary text-end font-medium'>{t('affiliate.date')}</div>
          <div className='text-text-secondary text-end font-medium'>{t('affiliate.amount')}</div>
        </div>
        {payments.map((payment, idx) => (
          <div key={idx} className='border-border-default grid grid-cols-2 gap-4 border-b p-3'>
            <div className='text-text-primary text-end'>{formatDate(payment.date)}</div>
            <div className='text-text-primary text-end'>{payment.payment}₪</div>
          </div>
        ))}
      </div>
    </AppDialog>
  )
}
const CARDS = [PaymentTypeEnum.Shipments, PaymentTypeEnum.Coupon, PaymentTypeEnum.Giftcard]

export const AffiliateProfitsTab = ({ refUserData }: AffiliateProfitsTabProps) => {
  const { t } = useTranslation()
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentTypeEnum | null>(null)
  const paymentsHistory = useMemo(() => refUserData?.paymentsHistory.filter(p => !!p.payment) || [], [refUserData])
  const coupons = refUserData?.coupons

  const totalPayments = useMemo(() => {
    return paymentsHistory?.filter(p => p.type !== PaymentTypeEnum.Giftcard).reduce((sum, p) => sum + parseFloat(p.payment || '0'), 0) || 0
  }, [paymentsHistory])

  const totalEarningsCoupons =
    (refUserData?.couponIncomeOffset || 0) + (coupons?.reduce((sum, coupon) => sum + Math.round(coupon.totalPaymentRevenue || 0), 0) || 0)

  const totalEarningsShipments = Math.round(refUserData?.pkgIncome || 0)

  const remainingPayments = totalEarningsCoupons + totalEarningsShipments - totalPayments

  const getPaymentsByType = useCallback(
    (type: PaymentTypeEnum) => paymentsHistory.filter(payment => payment.type === type),
    [paymentsHistory]
  )
  const calculateSum = (paymentsArray: AffPayment[]) => paymentsArray.reduce((sum, payment) => sum + +payment.payment, 0)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('he-IL')
  }

  const cardsData = useMemo(() => {
    return CARDS.map(type => {
      const config = paymentTypeConfigMap[type]
      return {
        type,
        title: t(config.labelKey),
        sum: calculateSum(getPaymentsByType(type)),
        cardBgClass: config.cardBgClass,
        iconColorClass: config.iconColorClass,
        Icon: config.Icon,
      }
    })
  }, [getPaymentsByType, t])

  return (
    <div className='space-y-4'>
      {paymentsHistory.length > 0 && (
        <>
          <Card className='bg-white'>
            <CardContent className='space-y-4'>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div className='bg-button-primary-selected-segment rounded-md p-3'>
                  <p className='text-text-secondary mb-1 text-sm'>{t('affiliate.paid')}</p>
                  <p className='text-text-primary text-base font-medium'>{totalPayments}₪</p>
                </div>
                <div className='bg-button-primary-selected-segment rounded-md p-3'>
                  <p className='text-text-secondary mb-1 text-sm'>{t('affiliate.remainingToPay')}</p>
                  <p className='text-text-primary text-base font-medium'>{remainingPayments}₪</p>
                </div>
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                {cardsData.map(({ type, title, sum, Icon, cardBgClass, iconColorClass }) => (
                  <button
                    key={type}
                    type='button'
                    onClick={() => setSelectedPaymentType(type)}
                    className={cn('text-text-secondary space-y-2 rounded-lg p-3', cardBgClass)}
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <div className='text-sm'>{title}</div>
                      <Icon className={`size-4 ${iconColorClass}`} />
                    </div>
                    <p className='text-start text-base leading-none font-medium'>{`₪${sum}`}</p>
                    <div className='flex items-center gap-1 text-sm'>
                      {t('affiliate.toPaymentDetails')}
                      <ArrowLeftIcon className='size-4 ltr:rotate-180' />
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className='bg-white'>
            <CardContent className='space-y-4'>
              <div className='text-text-secondary font-medium'>{t('affiliate.paymentHistory')}</div>
              <div className='border-border-default overflow-hidden rounded-lg border px-3'>
                <div className='border-border-default text-text-secondary grid grid-cols-3 gap-4 border-b py-3 text-sm font-medium'>
                  <div className='text-start'>{t('affiliate.status')}</div>
                  <div className='text-center'>{t('affiliate.amount')}</div>
                  <div className='text-end'>{t('affiliate.date')}</div>
                </div>
                {paymentsHistory.map((payment, idx) => {
                  const config = paymentTypeConfigMap[payment.type]
                  const Icon = config.Icon
                  return (
                    <div key={idx} className='border-border-default grid grid-cols-3 gap-4 border-b py-3 text-sm last:border-0'>
                      <div className='text-text-primary flex items-center justify-start gap-2'>
                        <span>{t('affiliate.paidStatus')}</span>
                        <Icon className={`size-4 ${config.iconColorClass}`} />
                      </div>
                      <div className='text-text-primary text-center'>{payment.payment}₪</div>
                      <div className='text-text-primary text-end'>{formatDate(payment.date)}</div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {selectedPaymentType && (
        <PaymentDetailsDialog
          paymentType={selectedPaymentType}
          payments={getPaymentsByType(selectedPaymentType)}
          isOpen={!!selectedPaymentType}
          onOpenChange={open => !open && setSelectedPaymentType(null)}
        />
      )}
    </div>
  )
}
